import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  AlertTriangle, AlertOctagon, CheckCircle2, Info, Loader2, Plus, Edit2, Trash2, X,
  ShieldAlert, Sparkles, Bomb, Zap, Eye, Target,
} from "lucide-react";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";

const CATEGORIES = [
  { id: "organisationnel", label: "Organisationnel" },
  { id: "financier", label: "Financier" },
  { id: "technologique", label: "Technologique" },
  { id: "humain", label: "Humain" },
  { id: "reglementaire", label: "Réglementaire" },
  { id: "strategique", label: "Stratégique" },
];

const AXES = [
  { key: "difficulte", label: "Difficulté", hint: "À quel point est-il difficile à résoudre" },
  { key: "urgence", label: "Urgence", hint: "Combien de temps avant qu'il devienne critique" },
  { key: "monetisabilite", label: "Monétisabilité", hint: "Peut-on en faire un business / une opportunité" },
  { key: "solvabilite", label: "Solvabilité", hint: "Est-il réellement résolvable avec les ressources actuelles" },
  { key: "volonte_reelle", label: "Volonté réelle", hint: "Y a-t-il une vraie volonté de le résoudre" },
];

const CLASS_STYLE = {
  bombe: { icon: Bomb, color: "#E24B4A", label: "Bombe à retardement" },
  prioritaire: { icon: AlertTriangle, color: "#EF9F27", label: "Prioritaire" },
  vigilant: { icon: Eye, color: "#378ADD", label: "Vigilant" },
  quick_win: { icon: Zap, color: "#1D9E75", label: "Quick win" },
  faible: { icon: CheckCircle2, color: "#71717a", label: "Faible" },
};

function ClassBadge({ value }) {
  const m = CLASS_STYLE[value] || CLASS_STYLE.faible;
  const Icon = m.icon;
  return (
    <span className="text-[10px] px-2 py-0.5 rounded uppercase tracking-wider inline-flex items-center gap-1"
      style={{ background: `${m.color}1A`, color: m.color, border: `1px solid ${m.color}40` }}>
      <Icon className="w-2.5 h-2.5" /> {m.label}
    </span>
  );
}

const EMPTY = {
  title: "", description: "", category: "organisationnel",
  difficulte: 5, urgence: 5, monetisabilite: 5, solvabilite: 5, volonte_reelle: 5,
  acteurs_impactes: "", temporalite: "court", impact_systemique: "local",
  qui_paiera: "", qui_souffre: "",
};

export default function ProblemGravity() {
  const { t } = useTranslation();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/workspace/problems");
      setItems(r.data.items || []);
    } catch (e) { setError(fmtError(e)); setItems([]); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (p) => {
    if (!window.confirm(t("saas.problem_gravity.confirm_delete"))) return;
    try { await api.delete(`/workspace/problems/${p.id}`); notify.success(t("saas.problem_gravity.toast_deleted")); await load(); }
    catch (e) { notify.error(fmtError(e)); }
  };

  const toRisk = async (p) => {
    if (!window.confirm(t("saas.problem_gravity.convert_to_risk") + " ?")) return;
    try {
      const r = await api.post(`/workspace/problems/${p.id}/to-risk`);
      notify.success(`${t("saas.problem_gravity.toast_converted")} ${r.data.code}`);
    } catch (e) { notify.error(fmtError(e)); }
  };

  const analyzePortfolio = async () => {
    setAnalyzing(true);
    try {
      const r = await api.post("/workspace/problems/analyze");
      setAnalysis(r.data);
      notify.success(t("saas.problem_gravity.toast_analyzed"));
    } catch (e) { notify.error(fmtError(e)); }
    finally { setAnalyzing(false); }
  };

  return (
    <div className="space-y-6" data-testid="pg-root">
      <ThreeQPageHeader endpoint="/workspace/gravites/top-priority" />
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#EF9F27] noam-overline">
            <Target className="w-3 h-3" /> {t("saas.problem_gravity.overline")}
          </div>
          <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">{t("saas.problem_gravity.title")}</h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-3xl">
            {t("saas.problem_gravity.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={analyzePortfolio} disabled={analyzing || (items?.length || 0) === 0}
            className="btn-ghost text-xs disabled:opacity-50" data-testid="pg-analyze-btn">
            {analyzing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {t("saas.problem_gravity.analyzing")}</> : <><Sparkles className="w-3.5 h-3.5" /> {t("saas.problem_gravity.analyze_btn")}</>}
          </button>
          <button onClick={() => setEditing("new")} className="btn-primary text-xs" data-testid="pg-create-btn">
            <Plus className="w-3.5 h-3.5" /> {t("saas.problem_gravity.modal_new")}
          </button>
        </div>
      </header>

      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{error}</span>
          <button className="ml-auto" onClick={() => setError(null)}><X className="w-3 h-3" /></button>
        </div>
      )}

      {analysis && <AnalysisCard analysis={analysis} items={items || []} onClose={() => setAnalysis(null)} />}

      {items === null && (
        <div className="noam-card p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-zinc-500" /></div>
      )}

      {items && items.length === 0 && (
        <div className="noam-card p-10 text-center" data-testid="pg-empty">
          <div className="w-14 h-14 mx-auto rounded-xl bg-[#EF9F27]/10 border border-[#EF9F27]/30 flex items-center justify-center">
            <Target className="w-6 h-6 text-[#EF9F27]" />
          </div>
          <h2 className="noam-display text-xl font-semibold mt-4">{t("saas.problem_gravity.empty_title")}</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            {t("saas.problem_gravity.empty_body")}
          </p>
          <button onClick={() => setEditing("new")} className="btn-primary text-sm mt-6 mx-auto inline-flex" data-testid="pg-empty-create">
            <Plus className="w-4 h-4" /> {t("saas.problem_gravity.create_first")}
          </button>
        </div>
      )}

      {items && items.length > 0 && (
        <>
          <PriorityMatrix items={items} />
          <div className="grid lg:grid-cols-2 gap-4" data-testid="pg-list">
            {items.map((p) => (
              <ProblemCard key={p.id} problem={p} onEdit={() => setEditing(p)} onDelete={() => remove(p)} onToRisk={() => toRisk(p)} />
            ))}
          </div>
        </>
      )}

      {editing && (
        <ProblemEditor problem={editing === "new" ? null : editing}
          onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />
      )}
    </div>
  );
}

function PriorityMatrix({ items }) {
  // Quadrants based on urgence (X) × gravity_score (Y)
  // bottom-left: low urg, low gravity (Ignore)
  // bottom-right: high urg, low gravity (Réactif)
  // top-left: low urg, high gravity (Stratégique)
  // top-right: high urg, high gravity (Action immédiate)
  const buckets = { tl: [], tr: [], bl: [], br: [] };
  items.forEach((p) => {
    const urg = p.urgence || 5;
    const grav = p.gravity_score || 50;
    if (grav >= 50 && urg >= 6) buckets.tr.push(p);
    else if (grav >= 50 && urg < 6) buckets.tl.push(p);
    else if (grav < 50 && urg >= 6) buckets.br.push(p);
    else buckets.bl.push(p);
  });
  return (
    <div className="noam-card p-5" data-testid="pg-matrix">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="noam-overline">Matrice de priorisation</div>
          <div className="noam-display text-base font-semibold">Urgence × Gravité</div>
        </div>
        <div className="text-[11px] text-zinc-500">{items.length} problème(s)</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Quadrant title="Stratégique" subtitle="Gravité forte · pas urgent" color="#7F77DD" items={buckets.tl} testid="quad-tl" />
        <Quadrant title="Action immédiate" subtitle="Gravité forte · urgent" color="#E24B4A" items={buckets.tr} testid="quad-tr" />
        <Quadrant title="Faible" subtitle="Gravité faible · pas urgent" color="#71717a" items={buckets.bl} testid="quad-bl" />
        <Quadrant title="Réactif" subtitle="Gravité faible · urgent" color="#EF9F27" items={buckets.br} testid="quad-br" />
      </div>
    </div>
  );
}

function Quadrant({ title, subtitle, color, items, testid }) {
  return (
    <div className="rounded-lg border p-4 min-h-[120px]" style={{ borderColor: `${color}40`, background: `${color}08` }} data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-sm font-semibold" style={{ color }}>{title}</div>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{subtitle}</div>
        </div>
        <div className="text-xl font-bold" style={{ color }}>{items.length}</div>
      </div>
      <ul className="space-y-1">
        {items.slice(0, 3).map((p) => (
          <li key={p.id} className="text-xs text-zinc-300 truncate flex items-center gap-1">
            <span className="w-1 h-1 rounded-full shrink-0" style={{ background: color }} />
            <span>{p.title}</span>
          </li>
        ))}
        {items.length > 3 && <li className="text-[10px] text-zinc-500">+{items.length - 3} autres</li>}
      </ul>
    </div>
  );
}

function AnalysisCard({ analysis, items, onClose }) {
  const findTitle = (id) => items.find((p) => p.id === id)?.title || id;
  return (
    <div className="noam-card p-5" data-testid="pg-analysis-card">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="noam-overline text-[#7F77DD]">Analyse NOAM — portefeuille</div>
          <div className="noam-display text-base font-semibold mt-1">Lecture stratégique</div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
      </div>
      {analysis.synthese && <p className="text-sm text-zinc-200 leading-relaxed mb-4">{analysis.synthese}</p>}
      <div className="grid md:grid-cols-3 gap-3">
        <AnalysisColumn label="Quick wins" items={analysis.quick_wins} color="#1D9E75" findTitle={findTitle} />
        <AnalysisColumn label="Bombes à retardement" items={analysis.bombes} color="#E24B4A" findTitle={findTitle} />
        <AnalysisColumn label="Opportunités cachées" items={analysis.opportunites_cachees} color="#EF9F27" findTitle={findTitle} />
      </div>
      {analysis.recommandations?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">Recommandations</div>
          <ul className="space-y-1">
            {analysis.recommandations.map((r, i) => (
              <li key={i} className="text-xs text-zinc-300 flex items-start gap-2">
                <span className="w-1 h-1 rounded-full mt-1.5 shrink-0 bg-[#7F77DD]" />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AnalysisColumn({ label, items, color, findTitle }) {
  if (!items || items.length === 0) return <div><div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color }}>{label}</div><div className="text-[11px] text-zinc-500 italic">—</div></div>;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider mb-1.5" style={{ color }}>{label}</div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="text-xs">
            <div className="text-zinc-200 font-medium">{findTitle(it.problem_id)}</div>
            <div className="text-zinc-500 text-[11px] mt-0.5">{it.raison}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ProblemCard({ problem, onEdit, onDelete, onToRisk }) {
  return (
    <div className="noam-card p-4" data-testid={`pg-card-${problem.id}`}>
      <div className="flex items-start gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <ClassBadge value={problem.classification} />
            <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-zinc-300">{problem.category}</span>
            <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-zinc-400">{problem.temporalite} terme</span>
          </div>
          <h3 className="text-sm text-white font-medium leading-snug">{problem.title}</h3>
        </div>
        <div className="text-2xl font-bold shrink-0" style={{ color: CLASS_STYLE[problem.classification]?.color || "#71717a" }}>
          {Math.round(problem.gravity_score || 0)}
        </div>
      </div>
      {problem.description && <p className="text-xs text-zinc-400 leading-relaxed line-clamp-2 mb-3">{problem.description}</p>}
      <div className="grid grid-cols-5 gap-1.5 mb-3">
        {AXES.map((a) => (
          <AxisBar key={a.key} label={a.label.split(" ")[0]} value={problem[a.key] || 5} />
        ))}
      </div>
      <div className="flex items-center justify-end gap-1 pt-2 border-t border-white/5">
        <button onClick={onToRisk} className="text-zinc-400 hover:text-[#E24B4A] p-1.5 rounded hover:bg-white/5 inline-flex items-center gap-1 text-[11px]" data-testid={`pg-to-risk-${problem.id}`}>
          <ShieldAlert className="w-3 h-3" /> Risque
        </button>
        <button onClick={onEdit} className="text-zinc-400 hover:text-white p-1.5 rounded hover:bg-white/5" data-testid={`pg-edit-${problem.id}`}>
          <Edit2 className="w-3.5 h-3.5" />
        </button>
        <button onClick={onDelete} className="text-zinc-500 hover:text-[#E24B4A] p-1.5 rounded hover:bg-white/5" data-testid={`pg-delete-${problem.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function AxisBar({ label, value }) {
  const pct = (value / 10) * 100;
  const color = value >= 8 ? "#E24B4A" : value >= 6 ? "#EF9F27" : value >= 4 ? "#378ADD" : "#1D9E75";
  return (
    <div className="text-center" title={`${label}: ${value}/10`}>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
      <div className="text-[9px] text-zinc-500 mt-1 uppercase tracking-wider truncate">{label}</div>
    </div>
  );
}

function ProblemEditor({ problem, onClose, onSaved }) {
  const isEdit = !!problem;
  const [form, setForm] = useState(() => problem ? {
    ...problem,
    acteurs_impactes: (problem.acteurs_impactes || []).join(", "),
  } : { ...EMPTY });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAxis = (k, v) => set(k, Math.max(1, Math.min(10, parseInt(v) || 5)));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        title: form.title.trim(),
        description: (form.description || "").trim(),
        category: form.category,
        difficulte: form.difficulte,
        urgence: form.urgence,
        monetisabilite: form.monetisabilite,
        solvabilite: form.solvabilite,
        volonte_reelle: form.volonte_reelle,
        acteurs_impactes: (form.acteurs_impactes || "").split(",").map((s) => s.trim()).filter(Boolean),
        temporalite: form.temporalite,
        impact_systemique: form.impact_systemique,
        qui_paiera: form.qui_paiera || "",
        qui_souffre: form.qui_souffre || "",
      };
      if (isEdit) {
        await api.patch(`/workspace/problems/${problem.id}`, payload);
        notify.success("Problème mis à jour.");
      } else {
        await api.post("/workspace/problems", payload);
        notify.success("Problème créé.");
      }
      onSaved();
    } catch (e) { setErr(fmtError(e)); }
    finally { setBusy(false); }
  };

  // Live preview gravity (client-side approximation)
  const previewBase = (form.difficulte + form.urgence + form.monetisabilite + form.solvabilite + form.volonte_reelle) / 5 * 10;
  const previewPenalty = (10 - form.volonte_reelle) * 1.5 + (10 - form.solvabilite) * 1.0;
  const previewGravity = Math.min(100, Math.round(previewBase * 0.7 + previewPenalty * 2.7));

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-3xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="pg-editor-modal">
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <div className="noam-overline">{isEdit ? "Modifier le problème" : "Nouveau problème"}</div>
            <div className="noam-display text-base font-semibold mt-0.5">{isEdit ? problem.title : "Cartographier un problème"}</div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">Gravité estimée</div>
              <div className="text-xl font-bold" style={{ color: previewGravity >= 75 ? "#E24B4A" : previewGravity >= 55 ? "#EF9F27" : previewGravity >= 35 ? "#378ADD" : "#1D9E75" }}>
                {previewGravity}/100
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <form onSubmit={submit} className="overflow-y-auto p-5 space-y-4">
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Titre *</label>
            <input required autoFocus value={form.title} onChange={(e) => set("title", e.target.value)}
              placeholder="Ex : Vendor lock-in critique sur le SI risque" className="noam-input" data-testid="pg-form-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Catégorie</label>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} className="noam-input" data-testid="pg-form-category">
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Temporalité</label>
              <select value={form.temporalite} onChange={(e) => set("temporalite", e.target.value)} className="noam-input">
                <option value="court">Court terme</option>
                <option value="moyen">Moyen terme</option>
                <option value="long">Long terme</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Description</label>
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2}
              placeholder="Contexte, manifestations, raisons pour lesquelles personne ne le traite." className="noam-input" />
          </div>

          <div className="border border-white/5 rounded-md p-4 bg-white/[0.02]">
            <div className="text-[10px] uppercase tracking-wider text-zinc-400 mb-3">Scoring sur 5 axes (1 = faible · 10 = maximum)</div>
            <div className="space-y-3">
              {AXES.map((a) => (
                <div key={a.key} className="flex items-center gap-3">
                  <div className="w-36 shrink-0">
                    <div className="text-xs text-zinc-200">{a.label}</div>
                    <div className="text-[10px] text-zinc-500">{a.hint}</div>
                  </div>
                  <input type="range" min="1" max="10" value={form[a.key]} onChange={(e) => setAxis(a.key, e.target.value)}
                    className="flex-1 accent-[#EF9F27]" data-testid={`pg-form-${a.key}`} />
                  <div className="w-8 text-right text-sm font-semibold text-white">{form[a.key]}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Impact systémique</label>
              <select value={form.impact_systemique} onChange={(e) => set("impact_systemique", e.target.value)} className="noam-input">
                <option value="local">Local</option>
                <option value="sectoriel">Sectoriel</option>
                <option value="systemique">Systémique</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Acteurs impactés (virgule)</label>
              <input value={form.acteurs_impactes} onChange={(e) => set("acteurs_impactes", e.target.value)}
                placeholder="DSI, Risk, Direction…" className="noam-input" />
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Qui souffre ?</label>
              <input value={form.qui_souffre} onChange={(e) => set("qui_souffre", e.target.value)}
                placeholder="Equipes ops, clients, direction…" className="noam-input" data-testid="pg-form-qui-souffre" />
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Qui paiera ?</label>
              <input value={form.qui_paiera} onChange={(e) => set("qui_paiera", e.target.value)}
                placeholder="Budget IT, métier, externe…" className="noam-input" data-testid="pg-form-qui-paiera" />
            </div>
          </div>

          {err && (
            <div className="flex items-start gap-2 p-2.5 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {err}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5 sticky bottom-0 bg-[var(--noam-bg)]">
            <button type="button" onClick={onClose} className="btn-ghost text-xs">Annuler</button>
            <button type="submit" disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid="pg-form-submit">
              {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enregistrement…</> : <>{isEdit ? "Enregistrer" : "Créer"}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
